'use client';
import { useState, useEffect, useRef } from 'react';
import Link from 'next/link';

const CARD_IMG_H = 200;
const SLIDE_COUNT = 2;
const SWIPE_THRESHOLD = 30;

export const CardImageSlider = ({ coverImage, videoCover, slug, productTitle, onPlayVideo, isNew, priority = false }) => {
    const [current, setCurrent] = useState(0);
    const [isMobile, setIsMobile] = useState(false);
    const touchStartX = useRef(null);

    useEffect(() => {
        const mq = window.matchMedia('(max-width: 767px)');
        setIsMobile(mq.matches);
        const onChange = (e) => setIsMobile(e.matches);
        mq.addEventListener('change', onChange);
        return () => mq.removeEventListener('change', onChange);
    }, []);

    const onTouchStart = (e) => {
        touchStartX.current = e.touches[0].clientX;
    };

    const onTouchEnd = (e) => {
        if (touchStartX.current === null) return;
        const delta = touchStartX.current - e.changedTouches[0].clientX;
        if (Math.abs(delta) < SWIPE_THRESHOLD) return;
        if (delta > 0) {
            // swipe left → next
            setCurrent((c) => Math.min(c + 1, SLIDE_COUNT - 1));
        } else {
            // swipe right → prev
            setCurrent((c) => Math.max(c - 1, 0));
        }
        touchStartX.current = null;
    };

    return (
        <div
            className="border-0 border-50 border-round-xl p-2 hover:border-100 bg-white"
            onMouseEnter={() => !isMobile && setCurrent(1)}
            onMouseLeave={() => !isMobile && setCurrent(0)}
            onTouchStart={isMobile ? onTouchStart : undefined}
            onTouchEnd={isMobile ? onTouchEnd : undefined}
        >
            <div style={{ position: 'relative', height: CARD_IMG_H, overflow: 'hidden', borderRadius: 8 }}>

                {/* ── New badge — top left ── */}
                {isNew && (
                    <span style={{
                        position: 'absolute', top: 0, left: 8, zIndex: 10,
                        background: '#ef4444', color: '#fff',
                        fontSize: '0.6rem', fontWeight: 700, letterSpacing: '0.05em',
                        padding: '4px 8px', borderRadius: 6,
                        boxShadow: '0 2px 8px rgba(0,0,0,0.2)',
                        pointerEvents: 'none',
                    }}>
                        NEW
                    </span>
                )}


                {/* ── Sliding track ── */}
                <div style={{
                    display: 'flex',
                    height: '100%',
                    transform: `translateX(-${current * 100}%)`,
                    transition: 'transform 0.6s cubic-bezier(0.4, 0, 0.2, 1)',
                }}>
                    {/* Slide 1 — Cover image */}
                    <div style={{ minWidth: '100%', height: '100%', flexShrink: 0 }}>
                        <Link
                            href={`/${slug}`}
                            style={{ display: 'block', width: '100%', height: '100%' }}
                            onClick={(e) => current !== 0 && e.preventDefault()}
                        >
                            {coverImage ? (
                                // eslint-disable-next-line @next/next/no-img-element
                                <img
                                    src={coverImage}
                                    alt={productTitle || 'Product image'}
                                    loading={priority ? 'eager' : 'lazy'}
                                    fetchPriority={priority ? 'high' : undefined}
                                    decoding="async"
                                    style={{ width: '100%', height: CARD_IMG_H, objectFit: 'contain', display: 'block' }}
                                />
                            ) : (
                                <div style={{
                                    width: '100%', height: CARD_IMG_H, background: '#f1f5f9',
                                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                                }}>
                                    <i className="pi pi-box" style={{ fontSize: '2rem', color: '#94a3b8' }} />
                                </div>
                            )}
                        </Link>
                    </div>

                    {/* Slide 2 — Video cover */}
                    <div
                        style={{ minWidth: '100%', height: '100%', flexShrink: 0, cursor: 'pointer' }}
                        onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPlayVideo?.(); }}
                    >
                        {/* eslint-disable-next-line @next/next/no-img-element */}
                        <img
                            src={videoCover || '/images/video-cover.png'}
                            alt="Product video"
                            loading="lazy"
                            decoding="async"
                            style={{ width: '100%', height: CARD_IMG_H, objectFit: 'cover', display: 'block' }}
                        />
                    </div>
                </div>

                {/* ── Slide indicators — mobile only ── */}
                {isMobile && (
                    <div style={{
                        position: 'absolute', bottom: 6, left: 0, right: 0, zIndex: 10,
                        display: 'flex', justifyContent: 'center', alignItems: 'center', gap: 5,
                        pointerEvents: 'none',
                    }}>
                        {Array.from({ length: SLIDE_COUNT }).map((_, i) => (
                            <span
                                key={i}
                                style={{
                                    height: 5,
                                    width: current === i ? 16 : 5,
                                    borderRadius: 3,
                                    background: current === i ? 'var(--primary-color)' : 'rgba(255,255,255,0.75)',
                                    boxShadow: '0 1px 3px rgba(0,0,0,0.3)',
                                    transition: 'width 0.35s ease, background 0.35s ease',
                                }}
                            />
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
};
